import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = {
  viewBox: "0 0 600 600",
  class: "relative w-full h-auto max-w-[560px] mx-auto",
  fill: "none"
}
const _hoisted_2 = { class: "hv-spin" }
const _hoisted_3 = {
  fill: "#1B2A4A",
  stroke: "url(#hv-stroke)",
  "stroke-width": "2.5"
}
const _hoisted_4 = ["transform"]
const _hoisted_5 = ["x2", "y2"]
const _hoisted_6 = ["transform"]
const _hoisted_7 = { class: "hv-scroll" }
const _hoisted_8 = { class: "hv-spin-rev" }
const _hoisted_9 = {
  fill: "#1B2A4A",
  stroke: "url(#hv-stroke)",
  "stroke-width": "3"
}
const _hoisted_10 = ["transform"]
const _hoisted_11 = ["transform"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { transform: "translate(468 452)" }

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";

// Interactive meshed gears: up to 4 small gears in fixed diagonal slots.
const MAX = 4;
const SCALE = 0.55;
const D = 132;

export default /*@__PURE__*/_defineComponent({
  __name: 'HeroVisual',
  setup(__props) {

const SLOTS = [-45, 45, 135, 225].map((deg, id) => {
  const r = (deg * Math.PI) / 180;
  return {
    id,
    x: +(300 + D * Math.cos(r)).toFixed(1),
    y: +(300 + D * Math.sin(r)).toFixed(1),
    bx: +(300 + (D + 52) * Math.cos(r)).toFixed(1),
    by: +(300 + (D + 52) * Math.sin(r)).toFixed(1),
  };
});
const activeIds = ref<number[]>([]);
const activeGears = computed(() => SLOTS.filter((s) => activeIds.value.includes(s.id)));
const addGear = () => {
  if (activeIds.value.length >= MAX) return;
  const free = SLOTS.find((s) => !activeIds.value.includes(s.id));
  if (free) activeIds.value.push(free.id);
};
const removeGear = (id: number) => {
  activeIds.value = activeIds.value.filter((x) => x !== id);
};

const wrapEl = ref<HTMLElement | null>(null);
const outerEl = ref<SVGGElement | null>(null);
const midEl = ref<SVGGElement | null>(null);
const innerEl = ref<SVGGElement | null>(null);
const gearEl = ref<SVGGElement | null>(null);

const teeth = [0, 45, 90, 135, 180, 225, 270, 315];

let target = 0;
let smooth = 0;
let rafId = 0;
let running = false;

// Scroll-rotation layers of the small gears (collected from the DOM since
// they are added/removed dynamically).
let smallEls: SVGGElement[] = [];
const refreshSmall = () => {
  smallEls = wrapEl.value
    ? Array.from(wrapEl.value.querySelectorAll<SVGGElement>(".hv-scroll"))
    : [];
};

// Transforms are written straight to the DOM here instead of via template
// bindings — going through Vue's renderer every frame is too slow for scroll.
const apply = () => {
  const deg = smooth * 0.12;
  if (wrapEl.value) wrapEl.value.style.transform = `translateY(${smooth * 0.06}px)`;
  if (outerEl.value) outerEl.value.style.transform = `rotate(${deg * 0.35}deg)`;
  if (midEl.value) midEl.value.style.transform = `rotate(${deg * -0.55}deg)`;
  if (innerEl.value) innerEl.value.style.transform = `rotate(${deg * 0.8}deg)`;
  if (gearEl.value) gearEl.value.style.transform = `rotate(${deg * 1.4}deg)`;
  // Small gears mesh with the central gear: counter-rotate, faster (smaller radius).
  const smallDeg = -deg * 1.4 * 1.82;
  for (const el of smallEls) el.style.transform = `rotate(${smallDeg}deg)`;
};

// Re-collect the small-gear layers whenever gears are added/removed,
// then immediately sync them to the current scroll rotation.
watch(
  () => activeIds.value.length,
  () => nextTick(() => { refreshSmall(); apply(); })
);

const tick = () => {
  smooth += (target - smooth) * 0.08;
  if (Math.abs(target - smooth) < 0.05) {
    smooth = target;
    apply();
    running = false;
    return;
  }
  apply();
  rafId = requestAnimationFrame(tick);
};

let visible = true;
let observer: IntersectionObserver | null = null;

const onScroll = () => {
  target = window.scrollY;
  if (!visible) return;
  if (!running) {
    running = true;
    rafId = requestAnimationFrame(tick);
  }
};

onMounted(() => {
  target = smooth = window.scrollY;
  apply();
  window.addEventListener("scroll", onScroll, { passive: true });

  // Off-screen: pause idle animations and skip scroll-driven updates entirely.
  if (wrapEl.value && "IntersectionObserver" in window) {
    observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        wrapEl.value?.classList.toggle("hv-paused", !visible);
        if (visible) {
          smooth = target = window.scrollY;
          apply();
        }
      },
      { rootMargin: "100px" }
    );
    observer.observe(wrapEl.value);
  }
});

onUnmounted(() => {
  window.removeEventListener("scroll", onScroll);
  cancelAnimationFrame(rafId);
  observer?.disconnect();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "wrapEl",
    ref: wrapEl,
    class: "relative select-none pointer-events-none will-change-transform"
  }, [
    _cache[14] || (_cache[14] = _createElementVNode("div", { class: "absolute inset-0 rounded-full glow-orb-10 scale-75" }, null, -1)),
    (_openBlock(), _createElementBlock("svg", _hoisted_1, [
      _cache[10] || (_cache[10] = _createStaticVNode("<defs data-v-1bea3e08><linearGradient id=\"hv-stroke\" x1=\"0\" y1=\"0\" x2=\"600\" y2=\"600\" gradientUnits=\"userSpaceOnUse\" data-v-1bea3e08><stop offset=\"0%\" stop-color=\"#3B82F6\" data-v-1bea3e08></stop><stop offset=\"100%\" stop-color=\"#60A5FA\" data-v-1bea3e08></stop></linearGradient><radialGradient id=\"hv-core\" cx=\"50%\" cy=\"50%\" r=\"50%\" data-v-1bea3e08><stop offset=\"0%\" stop-color=\"#3B82F6\" stop-opacity=\"0.35\" data-v-1bea3e08></stop><stop offset=\"100%\" stop-color=\"#3B82F6\" stop-opacity=\"0\" data-v-1bea3e08></stop></radialGradient></defs><circle cx=\"300\" cy=\"300\" r=\"170\" fill=\"url(#hv-core)\" data-v-1bea3e08></circle>", 2)),
      _createElementVNode("g", {
        ref_key: "outerEl",
        ref: outerEl,
        class: "hv-orbit"
      }, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("circle", {
          cx: "300",
          cy: "300",
          r: "262",
          stroke: "rgba(255,255,255,0.12)",
          "stroke-width": "1.5",
          "stroke-dasharray": "3 14"
        }, null, -1),
        _createElementVNode("circle", {
          cx: "300",
          cy: "38",
          r: "5",
          fill: "#60A5FA"
        }, null, -1),
        _createElementVNode("circle", {
          cx: "300",
          cy: "38",
          r: "11",
          stroke: "#60A5FA",
          "stroke-opacity": "0.35",
          "stroke-width": "1.5",
          class: "hv-pulse"
        }, null, -1),
        _createElementVNode("circle", {
          cx: "562",
          cy: "300",
          r: "4",
          fill: "rgba(255,255,255,0.5)"
        }, null, -1)
      ]))], 512),
      _createElementVNode("g", {
        ref_key: "midEl",
        ref: midEl,
        class: "hv-orbit"
      }, [...(_cache[1] || (_cache[1] = [
        _createStaticVNode("<g class=\"hv-spin-slow\" data-v-1bea3e08><circle cx=\"300\" cy=\"300\" r=\"205\" stroke=\"rgba(96,165,250,0.25)\" stroke-width=\"1.5\" data-v-1bea3e08></circle><path d=\"M 300 95 A 205 205 0 0 1 477 197\" stroke=\"url(#hv-stroke)\" stroke-width=\"3\" stroke-linecap=\"round\" data-v-1bea3e08></path><circle cx=\"477\" cy=\"197\" r=\"6\" fill=\"#3B82F6\" data-v-1bea3e08></circle><circle cx=\"123\" cy=\"403\" r=\"5\" fill=\"#60A5FA\" data-v-1bea3e08></circle><circle cx=\"123\" cy=\"403\" r=\"12\" stroke=\"#3B82F6\" stroke-opacity=\"0.3\" stroke-width=\"1.5\" class=\"hv-pulse\" data-v-1bea3e08></circle></g>", 1)
      ]))], 512),
      _createElementVNode("g", {
        ref_key: "innerEl",
        ref: innerEl,
        class: "hv-orbit"
      }, [...(_cache[2] || (_cache[2] = [
        _createElementVNode("circle", {
          cx: "300",
          cy: "300",
          r: "148",
          stroke: "rgba(255,255,255,0.1)",
          "stroke-width": "1",
          "stroke-dasharray": "40 22"
        }, null, -1),
        _createElementVNode("circle", {
          cx: "448",
          cy: "300",
          r: "4",
          fill: "#93C5FD"
        }, null, -1)
      ]))], 512),
      _cache[11] || (_cache[11] = _createElementVNode("g", {
        stroke: "rgba(96,165,250,0.18)",
        "stroke-width": "1"
      }, [
        _createElementVNode("line", {
          x1: "300",
          y1: "300",
          x2: "477",
          y2: "197"
        }),
        _createElementVNode("line", {
          x1: "300",
          y1: "300",
          x2: "123",
          y2: "403"
        }),
        _createElementVNode("line", {
          x1: "300",
          y1: "300",
          x2: "300",
          y2: "95"
        })
      ], -1)),
      _createElementVNode("g", {
        ref_key: "gearEl",
        ref: gearEl,
        class: "hv-orbit"
      }, [
        _createElementVNode("g", _hoisted_2, [
          _createElementVNode("g", _hoisted_3, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(teeth, (angle) => {
              return _createElementVNode("rect", {
                key: angle,
                x: "291",
                y: "212",
                width: "18",
                height: "24",
                rx: "4",
                transform: `rotate(${angle} 300 300)`
              }, null, 8, _hoisted_4)
            }), 64)),
            _cache[3] || (_cache[3] = _createElementVNode("circle", {
              cx: "300",
              cy: "300",
              r: "68"
            }, null, -1))
          ]),
          _cache[4] || (_cache[4] = _createElementVNode("circle", {
            cx: "300",
            cy: "300",
            r: "42",
            stroke: "rgba(96,165,250,0.4)",
            "stroke-width": "1.5",
            "stroke-dasharray": "6 8"
          }, null, -1))
        ])
      ], 512),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeGears.value, (g) => {
        return (_openBlock(), _createElementBlock("g", {
          key: g.id
        }, [
          _createElementVNode("line", {
            x1: "300",
            y1: "300",
            x2: g.x,
            y2: g.y,
            stroke: "rgba(96,165,250,0.25)",
            "stroke-width": "1.5"
          }, null, 8, _hoisted_5),
          _createElementVNode("g", {
            transform: `translate(${g.x} ${g.y}) scale(${SCALE})`
          }, [
            _createElementVNode("g", _hoisted_7, [
              _createElementVNode("g", _hoisted_8, [
                _createElementVNode("g", _hoisted_9, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(teeth, (angle) => {
                    return _createElementVNode("rect", {
                      key: angle,
                      x: "-9",
                      y: "-88",
                      width: "18",
                      height: "24",
                      rx: "4",
                      transform: `rotate(${angle})`
                    }, null, 8, _hoisted_10)
                  }), 64)),
                  _cache[5] || (_cache[5] = _createElementVNode("circle", { r: "68" }, null, -1))
                ]),
                _cache[6] || (_cache[6] = _createElementVNode("circle", {
                  r: "40",
                  stroke: "rgba(96,165,250,0.4)",
                  "stroke-width": "2",
                  "stroke-dasharray": "6 8"
                }, null, -1))
              ])
            ]),
            _cache[7] || (_cache[7] = _createElementVNode("circle", {
              r: "11",
              fill: "#3B82F6"
            }, null, -1))
          ], 8, _hoisted_6),
          _createElementVNode("g", {
            transform: `translate(${g.bx} ${g.by})`
          }, [
            _createElementVNode("g", {
              class: "hv-ctrl pointer-events-auto cursor-pointer",
              onClick: ($event: any) => (removeGear(g.id))
            }, [...(_cache[8] || (_cache[8] = [
              _createElementVNode("circle", {
                r: "13",
                fill: "#1B2A4A",
                stroke: "#60A5FA",
                "stroke-width": "1.5"
              }, null, -1),
              _createElementVNode("path", {
                d: "M -4.5 -4.5 L 4.5 4.5 M 4.5 -4.5 L -4.5 4.5",
                stroke: "#fff",
                "stroke-width": "2",
                "stroke-linecap": "round"
              }, null, -1)
            ]))], 8, _hoisted_12)
          ], 8, _hoisted_11)
        ]))
      }), 128)),
      _createElementVNode("g", _hoisted_13, [
        (activeGears.value.length < MAX)
          ? (_openBlock(), _createElementBlock("g", {
              key: 0,
              class: "hv-ctrl pointer-events-auto cursor-pointer",
              onClick: addGear
            }, [...(_cache[9] || (_cache[9] = [
              _createElementVNode("circle", {
                r: "18",
                fill: "#3B82F6"
              }, null, -1),
              _createElementVNode("circle", {
                r: "18",
                fill: "none",
                stroke: "#60A5FA",
                "stroke-opacity": "0.6",
                "stroke-width": "2",
                class: "hv-pulse"
              }, null, -1),
              _createElementVNode("path", {
                d: "M -8 0 H 8 M 0 -8 V 8",
                stroke: "#fff",
                "stroke-width": "2.5",
                "stroke-linecap": "round"
              }, null, -1)
            ]))]))
          : _createCommentVNode("", true)
      ]),
      _cache[12] || (_cache[12] = _createElementVNode("circle", {
        cx: "300",
        cy: "300",
        r: "14",
        fill: "#3B82F6"
      }, null, -1)),
      _cache[13] || (_cache[13] = _createElementVNode("circle", {
        cx: "300",
        cy: "300",
        r: "22",
        stroke: "#60A5FA",
        "stroke-opacity": "0.5",
        "stroke-width": "1.5",
        class: "hv-pulse"
      }, null, -1))
    ]))
  ], 512))
}
}

})